import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Badge, Input, Alert, Modal, message, Typography } from 'antd';
import { SearchOutlined, EyeOutlined, DeleteOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './SupportRequests.css';

const { Paragraph, Text } = Typography;

const SupportRequests = () => {
  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const navigate = useNavigate();

  const fetchConnectedRecords = async () => {
    setLoading(true);
    try {
      console.log('Sending request to /backend/admin_get_connected_support_records.php');
      const response = await fetch('/backend/admin_get_connected_support_records.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      console.log('HTTP Status:', response.status);
      const text = await response.text();
      console.log('Raw response:', text);
      let data;
      try {
        data = JSON.parse(text);
        console.log('Parsed data:', data);
      } catch (e) {
        console.error('JSON parsing error:', e, 'Received text:', text);
        throw new Error('Non-JSON response');
      }
      if (response.ok) {
        // Parse JSON fields
        const parsedData = data.map(record => ({
          ...record,
          needs: record.needs ? JSON.parse(record.needs) : [],
          connections: record.connections ? JSON.parse(record.connections) : [],
          admin_connections: record.admin_connections ? JSON.parse(record.admin_connections) : [],
        }));
        setDataSource(parsedData);
        setFilteredData(parsedData);
        setError(null);
      } else {
        if (response.status === 403) {
          console.log('Error 403: Session expired');
          message.error('Session expired. Please log in again.');
          navigate('/adminlogin');
        } else {
          setError(data.error || `HTTP error ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Network error:', error.message);
      setError(error.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceActivities = async () => {
    setLoading(true);
    try {
      const url = '/backend/admin_get_service_activities.php';
      console.log('Sending request:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      console.log('HTTP Status:', response.status);
      const text = await response.text();
      console.log('Raw response:', text);
      let data;
      try {
        data = JSON.parse(text);
        console.log('Parsed data:', data);
      } catch (e) {
        console.error('JSON parsing error:', e, 'Received text:', text);
        throw new Error('Non-JSON response');
      }
      if (response.ok) {
        console.log('Activities received:', data.activities);
        setActivities(data.activities || []);
        setFilteredActivities(data.activities || []);
        setError(null);
      } else {
        if (response.status === 403) {
          console.log('Error 403: Session expired');
          message.error('Session expired. Please log in again.');
          navigate('/adminlogin');
        } else {
          setError(data.error || `HTTP error ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Network error:', error.message);
      setError(`Network error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSupport = (record) => {
    Modal.confirm({
      title: 'Delete Confirmation',
      content: `Are you sure you want to delete record ID ${record.id}?`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await fetch('/backend/admin_delete_support_record.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ id: record.id }),
          });
          const data = await response.json();
          if (response.ok) {
            message.success('Record deleted successfully');
            setDataSource(dataSource.filter((item) => item.id !== record.id));
            setFilteredData(filteredData.filter((item) => item.id !== record.id));
          } else {
            message.error(data.error || 'Error during deletion');
          }
        } catch (error) {
          console.error('Network error during deletion:', error);
          message.error('Network error during deletion');
        }
      },
    });
  };

  const handleDeleteActivity = (record) => {
    Modal.confirm({
      title: 'Delete Confirmation',
      content: `Are you sure you want to delete activity ID ${record.id}?`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await fetch('/backend/admin_delete_service_activity.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ id: record.id }),
          });
          const data = await response.json();
          if (response.ok) {
            message.success('Activity deleted successfully');
            setActivities(activities.filter((item) => item.id !== record.id));
            setFilteredActivities(filteredActivities.filter((item) => item.id !== record.id));
          } else {
            message.error(data.error || 'Error during deletion');
          }
        } catch (error) {
          console.error('Network error during deletion:', error);
          message.error('Network error during deletion');
        }
      },
    });
  };

  const handleViewSupport = (record) => {
    setSelectedRecord({ type: 'support', data: record });
    setIsModalVisible(true);
  };

  const handleViewActivity = (activity) => {
    setSelectedRecord({ type: 'activity', data: activity });
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  useEffect(() => {
    fetchConnectedRecords();
    fetchServiceActivities();
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    const lowerValue = value.toLowerCase();

    // Filter support requests
    const filteredSupports = dataSource.filter((record) =>
      [
        record.id,
        record.user_email,
        record.support_number,
        record.record_type,
        record.status,
        record.created_at,
        ...(record.needs || []),
        ...(record.connections || []),
        ...(record.admin_connections || []),
      ].some((field) => String(field).toLowerCase().includes(lowerValue))
    );
    setFilteredData(filteredSupports);

    // Filter service activities
    const filteredActivities = activities.filter((activity) =>
      Object.values(activity).some((field) =>
        String(field).toLowerCase().includes(lowerValue)
      ) ||
      (activity.documents && activity.documents.some((doc) =>
        String(doc.description).toLowerCase().includes(lowerValue)
      ))
    );
    setFilteredActivities(filteredActivities);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value) || 10);
  };

  const supportColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'User Email', dataIndex: 'user_email', key: 'user_email', width: 200 },
    { title: 'Support Number', dataIndex: 'support_number', key: 'support_number', width: 150 },
    { title: 'Record Type', dataIndex: 'record_type', key: 'record_type', width: 150 },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge
          count={status}
          style={{
            backgroundColor: status === 'Pending' ? '#faad14' : '#52c41a',
            padding: '4px 12px',
            borderRadius: '4px',
            fontSize: '12px',
          }}
        />
      ),
      width: 120,
    },
    { title: 'Created At', dataIndex: 'created_at', key: 'created_at', width: 150 },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            className="action-btn view-btn"
            onClick={() => handleViewSupport(record)}
          />
          <Button
            type="danger"
            icon={<DeleteOutlined />}
            className="action-btn delete-btn"
            onClick={() => handleDeleteSupport(record)}
          />
        </Space>
      ),
      width: 150,
    },
  ];

  const activityColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Activity Date', dataIndex: 'activity_date', key: 'activity_date', width: 120 },
    { title: 'Location', dataIndex: 'location', key: 'location', width: 150 },
    { title: 'Service Provider', dataIndex: 'service_provider', key: 'service_provider', width: 200 },
    { title: 'Primary Staff', dataIndex: 'primary_staff', key: 'primary_staff', width: 150 },
    { title: 'Secondary Staff', dataIndex: 'secondary_staff', key: 'secondary_staff', width: 150 },
    { title: 'Staff Name', dataIndex: 'staff_name', key: 'staff_name', width: 150 },
    { title: 'Activity Type', dataIndex: 'activity_type', key: 'activity_type', width: 120 },
    { title: 'Service Delivery', dataIndex: 'service_delivery', key: 'service_delivery', width: 150 },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', width: 200 },
    { title: 'Created At', dataIndex: 'created_at', key: 'created_at', width: 150 },
    {
      title: 'Documents',
      key: 'documents',
      render: (_, record) => (
        <Space direction="vertical">
          {record.documents && record.documents.length > 0 ? (
            record.documents.map((doc) => (
              <a
                key={doc.id}
                href={`/backend/${doc.file_path.replace(/^Uploads\//, '')}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {doc.description} <DownloadOutlined />
              </a>
            ))
          ) : (
            <Text type="secondary">No documents</Text>
          )}
        </Space>
      ),
      width: 200,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            className="action-btn view-btn"
            onClick={() => handleViewActivity(record)}
          />
          <Button
            type="danger"
            icon={<DeleteOutlined />}
            className="action-btn delete-btn"
            onClick={() => handleDeleteActivity(record)}
          />
        </Space>
      ),
      width: 150,
    },
  ];

  return (
    <div className="support-requests-container">
      <h2 className="section-title">Manage Incoming Requests</h2>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 20 }} />}
      <div className="table-header">
        <span>
          Show <Input defaultValue="10" style={{ width: 60, margin: '0 8px' }} onChange={(e) => handlePageSizeChange(e.target.value)} /> entries
        </span>
        <Space>
          <Input
            placeholder="Search..."
            prefix={<SearchOutlined />}
            style={{ width: 200 }}
            className="search-input"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
          />
          <Button
            icon={<ReloadOutlined />}
            type="primary"
            onClick={() => {
              fetchConnectedRecords();
              fetchServiceActivities();
            }}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>
      <Table
        columns={supportColumns}
        dataSource={filteredData}
        pagination={{ pageSize, showSizeChanger: false, position: ['bottomRight'] }}
        className="table-style"
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-even' : 'table-row-odd')}
        loading={loading}
      />

      <h2 className="section-title" style={{ marginTop: 40 }}>My Service Activities</h2>
      {filteredActivities.length > 0 ? (
        <Table
          columns={activityColumns}
          dataSource={filteredActivities}
          pagination={{ pageSize, showSizeChanger: false, position: ['bottomRight'] }}
          scroll={{ x: 'max-content' }}
          className="table-style"
          rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-even' : 'table-row-odd')}
          loading={loading}
        />
      ) : (
        <Alert
          message="No Activities Found"
          description="You have no service activities assigned to your account."
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />
      )}

      <Modal
        title={selectedRecord?.type === 'support' ? `Record Details ID ${selectedRecord?.data.id}` : `Activity Details ID ${selectedRecord?.data.id}`}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalOk}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {selectedRecord?.type === 'support' && selectedRecord.data && (
          <div>
            <Paragraph><Text strong>User Email:</Text> {selectedRecord.data.user_email}</Paragraph>
            <Paragraph><Text strong>Support Number:</Text> {selectedRecord.data.support_number || 'N/A'}</Paragraph>
            <Paragraph><Text strong>Record Type:</Text> {selectedRecord.data.record_type || 'N/A'}</Paragraph>
            <Paragraph><Text strong>Status:</Text> {selectedRecord.data.status || 'N/A'}</Paragraph>
            <Paragraph><Text strong>Created At:</Text> {selectedRecord.data.created_at}</Paragraph>
            <Paragraph>
              <Text strong>Needs:</Text>
              {selectedRecord.data.needs.length > 0 ? (
                <ul>
                  {selectedRecord.data.needs.map((need, index) => (
                    <li key={index}>{need}</li>
                  ))}
                </ul>
              ) : (
                ' None'
              )}
            </Paragraph>
            <Paragraph>
              <Text strong>Connections (Services):</Text>
              {selectedRecord.data.connections.length > 0 ? (
                <ul>
                  {selectedRecord.data.connections.map((connection, index) => (
                    <li key={index}>{connection}</li>
                  ))}
                </ul>
              ) : (
                ' None'
              )}
            </Paragraph>
            <Paragraph>
              <Text strong>Connections (Admins):</Text>
              {selectedRecord.data.admin_connections.length > 0 ? (
                <ul>
                  {selectedRecord.data.admin_connections.map((admin, index) => (
                    <li key={index}>{admin}</li>
                  ))}
                </ul>
              ) : (
                ' None'
              )}
            </Paragraph>
          </div>
        )}
        {selectedRecord?.type === 'activity' && selectedRecord.data && (
          <div>
            <Paragraph><Text strong>ID:</Text> {selectedRecord.data.id}</Paragraph>
            <Paragraph><Text strong>Activity Date:</Text> {selectedRecord.data.activity_date}</Paragraph>
            <Paragraph><Text strong>Location:</Text> {selectedRecord.data.location}</Paragraph>
            <Paragraph><Text strong>Service Provider:</Text> {selectedRecord.data.service_provider}</Paragraph>
            <Paragraph><Text strong>Primary Staff:</Text> {selectedRecord.data.primary_staff}</Paragraph>
            <Paragraph><Text strong>Secondary Staff:</Text> {selectedRecord.data.secondary_staff || 'N/A'}</Paragraph>
            <Paragraph><Text strong>Staff Name:</Text> {selectedRecord.data.staff_name || 'N/A'}</Paragraph>
            <Paragraph><Text strong>Activity Type:</Text> {selectedRecord.data.activity_type}</Paragraph>
            <Paragraph><Text strong>Service Delivery:</Text> {selectedRecord.data.service_delivery}</Paragraph>
            <Paragraph><Text strong>Notes:</Text> {selectedRecord.data.notes || 'None'}</Paragraph>
            <Paragraph><Text strong>Created At:</Text> {selectedRecord.data.created_at}</Paragraph>
            <Paragraph>
              <Text strong>Documents:</Text>
              {selectedRecord.data.documents && selectedRecord.data.documents.length > 0 ? (
                <ul>
                  {selectedRecord.data.documents.map((doc) => (
                    <li key={doc.id}>
                      <a
                        href={`/backend/${doc.file_path.replace(/^Uploads\//, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {doc.description} <DownloadOutlined />
                      </a>
                      <br />
                      <Text type="secondary">Added on: {doc.created_at}</Text>
                    </li>
                  ))}
                </ul>
              ) : (
                ' None'
              )}
            </Paragraph>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SupportRequests;